// Entities.tsx (v4) — PWA V2 slice 3. Entity browser: pick a type, list live
// entities, tap one to open the universal drill-through Sheet (recursive graph
// descent). Digital thread health chips shown above the browser.
//
// STEP-OV-ENTITY-DOCS-BROWSER:
//   - Docs are a browsable type in the entity browser (already, via BROWSABLE).
//   - Status filter chips added: Complete, In Progress, Blocked, Proposed.
//   - Milestone filter chip added: enter a milestone ID to narrow by milestone.

import { useCallback, useEffect, useState } from "react";
import { fetchEntities, fetchDigitalThreadHealth } from "../../api/client.js";
import type { DigitalThreadHealthResponse } from "../../api/types.js";
import { WikiView } from "../../screens/Entities.js";
import { TYPE_META } from "../entityTypes.js";
import { useTimeShift } from "../TimeShift.js";
import { openDocSheet } from "../DocsSheet.js";

// Slice 4 (STEP-PWA-UXV2-INTEGRATE-LEGACY): two view-modes. "Browse" is the
// v4 live browser + Knowledge Health hero; "Type Reference" reuses the legacy
// WikiView (entity schema). The legacy "Docs" view-mode has been folded into
// the browse type picker as the "Docs" type (STEP-OV-ENTITY-DOCS-BROWSER).
type ViewMode = "browse" | "wiki";

const VIEW_MODES: { id: ViewMode; label: string }[] = [
  { id: "browse", label: "Browse" },
  { id: "wiki", label: "Type Reference" },
];

const BROWSABLE: { key: string; label: string }[] = [
  { key: "steps", label: "Steps" },
  { key: "milestones", label: "Milestones" },
  { key: "vlrs", label: "VLRs" },
  { key: "decisions", label: "Decisions" },
  { key: "spikes", label: "Spikes" },
  { key: "milestone-reviews", label: "Reviews" },
  { key: "docs", label: "Docs" },
];

// Status filter chips — quick filters applied client-side after fetch.
const STATUS_FILTERS: { key: string; label: string }[] = [
  { key: "complete", label: "Complete" },
  { key: "in_progress", label: "In Progress" },
  { key: "blocked", label: "Blocked" },
  { key: "proposed", label: "Proposed" },
];

const STATUS_CHIP: Record<string, string> = {
  complete: "ok",
  integrated: "ok",
  active: "accent",
  in_progress: "accent",
  proposed: "warn",
  pending: "warn",
  triaged: "warn",
  failed: "danger",
  rejected: "danger",
};

function statusChipClass(status: string): string {
  const cls = STATUS_CHIP[status?.toLowerCase()] ?? "";
  return cls ? `chip ${cls}` : "chip";
}

type SortKey = "id" | "title" | "status" | "updated";

export function EntitiesScreen() {
  const { asOf, isLive } = useTimeShift();
  const [viewMode, setViewMode] = useState<ViewMode>("browse");
  const [type, setType] = useState<string>("steps");
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [threadHealth, setThreadHealth] = useState<DigitalThreadHealthResponse | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState<SortKey>("id");
  // Status filter chip — null means "all statuses"
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  // Milestone filter — empty string means "no filter"
  const [milestoneFilter, setMilestoneFilter] = useState("");
  const [milestoneInput, setMilestoneInput] = useState("");

  useEffect(() => {
    fetchDigitalThreadHealth().then((t) => setThreadHealth(t)).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(
    async (t: string) => {
      setLoading(true);
      setError(false);
      try {
        // Scrubbed mode reconstructs the list "as of" that moment from the
        // temporal entity_versions archive (statuses/titles as they were).
        // For docs, also pass server-side search so the large file list is
        // pre-filtered on the server before the client-side pass runs.
        const opts: { asOf?: string; search?: string } = {};
        if (asOf) opts.asOf = asOf;
        if (debouncedSearch) opts.search = debouncedSearch;
        const res = await fetchEntities(t, opts);
        setRows(res.entities);
      } catch {
        setError(true);
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [asOf, debouncedSearch],
  );

  // Reset search and filters when type changes.
  useEffect(() => {
    setSearch("");
    setDebouncedSearch("");
    setStatusFilter(null);
    setMilestoneFilter("");
    setMilestoneInput("");
  }, [type]);

  useEffect(() => {
    void load(type);
  }, [type, load]);

  // Apply client-side sort, status filter, and milestone filter.
  const displayRows = rows
    .filter((r: Record<string, unknown>) => {
      if (statusFilter) {
        const rowStatus = String(r.status ?? "").toLowerCase();
        if (rowStatus !== statusFilter) return false;
      }
      if (milestoneFilter) {
        const rowMilestone = String(r.milestone_id ?? r.milestone ?? "").toLowerCase();
        if (!rowMilestone.includes(milestoneFilter.toLowerCase())) return false;
      }
      return true;
    })
    .sort((a: Record<string, unknown>, b: Record<string, unknown>) => {
      if (sortBy === "title") return String(a.title ?? "").localeCompare(String(b.title ?? ""));
      if (sortBy === "status") return String(a.status ?? "").localeCompare(String(b.status ?? ""));
      if (sortBy === "updated")
        return String(b.updated_at ?? "").localeCompare(String(a.updated_at ?? ""));
      return String(a.id ?? "").localeCompare(String(b.id ?? ""));
    });

  return (
    <div style={{ paddingBottom: 80 }}>
      {!isLive && (
        <div
          style={{
            padding: "8px 20px 0",
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--text-dim)",
          }}
        >
          ● thread health is LIVE; browse list is as-of
        </div>
      )}

      {/* View-mode switcher: Browse | Type Reference */}
      <div
        style={{
          display: "flex",
          gap: 6,
          padding: "14px 20px 0",
        }}
      >
        {VIEW_MODES.map((m) => (
          <button
            key={m.id}
            onClick={() => setViewMode(m.id)}
            className={viewMode === m.id ? "chip accent" : "chip"}
            style={{ cursor: "pointer", whiteSpace: "nowrap" }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {viewMode === "wiki" && (
        <div style={{ paddingTop: 16 }}>
          <WikiView />
        </div>
      )}

      {viewMode === "browse" && threadHealth && (
        <div style={{ display: "flex", gap: 6, padding: "4px 20px 8px", flexWrap: "wrap" }}>
          <span className="chip" style={{ fontSize: 10, fontFamily: "var(--font-mono)" }}>
            🧵 thread {threadHealth.annotationCoverage}%
          </span>
          <span className="chip" style={{ fontSize: 10, fontFamily: "var(--font-mono)" }}>
            ⊙ embed {threadHealth.embeddingCoverage ?? 0}%
          </span>
          {threadHealth.orphanCount > 0 && (
            <span className="chip warn" style={{ fontSize: 10, fontFamily: "var(--font-mono)" }}>
              ⚠ {threadHealth.orphanCount} orphan{threadHealth.orphanCount !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}

      {viewMode === "browse" && (
        <>
      <div className="section-head">
        <h2>Browse</h2>
        {!loading && !error && <span className="count">{displayRows.length}</span>}
      </div>

      {/* Type picker chips */}
      <div
        style={{
          display: "flex",
          gap: 6,
          overflowX: "auto",
          padding: "14px 20px",
          borderBottom: "1px solid var(--border-soft)",
        }}
      >
        {BROWSABLE.map((b) => (
          <button
            key={b.key}
            onClick={() => setType(b.key)}
            className={type === b.key ? "chip accent" : "chip"}
            style={{ cursor: "pointer", whiteSpace: "nowrap" }}
          >
            {b.label}
          </button>
        ))}
      </div>

      {/* Status filter chips — hidden for docs (no status field) */}
      {type !== "docs" && (
        <div
          style={{
            display: "flex",
            gap: 6,
            overflowX: "auto",
            padding: "8px 20px 4px",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: "var(--text-dim)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              flexShrink: 0,
            }}
          >
            Status
          </span>
          <button
            onClick={() => setStatusFilter(null)}
            className={statusFilter === null ? "chip accent" : "chip"}
            style={{ cursor: "pointer", whiteSpace: "nowrap", fontSize: 10 }}
          >
            All
          </button>
          {STATUS_FILTERS.map((sf) => (
            <button
              key={sf.key}
              onClick={() => setStatusFilter(statusFilter === sf.key ? null : sf.key)}
              className={statusFilter === sf.key ? "chip accent" : "chip"}
              style={{ cursor: "pointer", whiteSpace: "nowrap", fontSize: 10 }}
            >
              {sf.label}
            </button>
          ))}
        </div>
      )}

      {/* Milestone filter — shown for steps only (steps have milestone_id) */}
      {type === "steps" && (
        <div
          style={{
            display: "flex",
            gap: 6,
            padding: "4px 20px 4px",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: "var(--text-dim)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              flexShrink: 0,
            }}
          >
            Milestone
          </span>
          <input
            type="text"
            placeholder="M-XXX"
            value={milestoneInput}
            onChange={(e) => setMilestoneInput((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setMilestoneFilter(milestoneInput.trim());
              if (e.key === "Escape") { setMilestoneInput(""); setMilestoneFilter(""); }
            }}
            style={{
              flex: 1,
              maxWidth: 140,
              background: "var(--bg-elev)",
              border: milestoneFilter ? "1px solid var(--accent)" : "1px solid var(--border-soft)",
              borderRadius: 6,
              padding: "4px 8px",
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--text)",
            }}
          />
          {milestoneFilter && (
            <button
              onClick={() => { setMilestoneFilter(""); setMilestoneInput(""); }}
              className="chip"
              style={{ cursor: "pointer", fontSize: 10 }}
            >
              × clear
            </button>
          )}
          {milestoneInput && milestoneInput !== milestoneFilter && (
            <button
              onClick={() => setMilestoneFilter(milestoneInput.trim())}
              className="chip accent"
              style={{ cursor: "pointer", fontSize: 10 }}
            >
              Filter
            </button>
          )}
        </div>
      )}

      {/* Search and sort controls */}
      <div style={{ display: "flex", gap: 6, padding: "8px 20px 0", alignItems: "center" }}>
        <input
          type="text"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            background: "var(--bg-elev)",
            border: "1px solid var(--border-soft)",
            borderRadius: 6,
            padding: "6px 10px",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--text)",
          }}
        />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortKey)}
          style={{
            background: "var(--bg-elev)",
            border: "1px solid var(--border-soft)",
            borderRadius: 6,
            padding: "6px 8px",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--text)",
          }}
        >
          <option value="id">ID</option>
          <option value="title">Title</option>
          <option value="status">Status</option>
          <option value="updated">Updated</option>
        </select>
      </div>

      {loading && (
        <div
          style={{
            padding: "40px 20px",
            textAlign: "center",
            color: "var(--text-dim)",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
          }}
        >
          Loading {TYPE_META[type]?.label ?? type}…
        </div>
      )}

      {error && !loading && (
        <div
          style={{
            padding: "40px 20px",
            textAlign: "center",
            color: "var(--danger)",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
          }}
        >
          ⚠ Could not load entities
        </div>
      )}

        {!loading &&
          !error &&
          displayRows.map((r) => {
            const id = r.id as string;
            const title = r.title as string | null;
            const status = r.status as string | null;
            // Docs: show human-readable path instead of opaque base64url id.
            const displayId = type === "docs"
              ? (r.path as string | null) ?? id
              : id;
            if (type === "docs") {
              const docPath = (r.path as string | null) ?? "";
              const docTitle = title ?? displayId;
              return (
                <button
                  key={id}
                  className="card"
                  style={{ display: "block", width: "100%", textAlign: "left", margin: "0 0 1px", borderRadius: 0, cursor: "pointer" }}
                  onClick={() => openDocSheet(docPath, docTitle)}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="mono" style={{ color: "var(--text-dim)", fontSize: 10 }}>{displayId}</span>
                    <span style={{ flex: 1, fontSize: 12.5, lineHeight: 1.35 }}>{title}</span>
                  </div>
                </button>
              );
            }
            return (
              <button
                key={id}
                className="card"
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  cursor: "pointer",
                  margin: "0 0 1px",
                  borderRadius: 0,
                }}
                onClick={() => window.__openEntity?.(type, id)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="mono" style={{ color: "var(--accent)", fontSize: 10 }}>
                    {id}
                  </span>
                  <span style={{ flex: 1, fontSize: 12.5, lineHeight: 1.35 }}>{title}</span>
                  {status && <span className={statusChipClass(status)}>{status}</span>}
                </div>
              </button>
            );
          })}
        </>
      )}
    </div>
  );
}
